import type { DateTime } from "luxon";
import type { SpansState } from "@/store/spans/state";
import type { PerspectiveViewConfig } from "@/models/perspective";
import type { SpansInMemoryFilters } from "@/store/spans/state";

/**
 * Retrieve the latest datetime at which a query was started from the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - The datetime that indicates when the latest query started or undefined.
 */
export function getLatestQueryStartedAt(
  state: SpansState
): DateTime | undefined {
  return state.latestQueryStartedAt;
}

/**
 * Retrieve the loading state of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - Boolean value that indicates if there are currently values being fetched.
 */
export function getLoading(state: SpansState): boolean | undefined {
  return state.loading;
}

/**
 * Retrieve the boolean value that checks if the query must be executed.
 * @param state - Vuex state for the Spans module.
 * @returns - Boolean value that indicates if the current query must be executed.
 */
export function getShouldExecuteQuery(state: SpansState): boolean | undefined {
  return state.shouldExecuteQuery;
}

/**
 * Retrieve the updateSpans value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - The updateSpans number value if defined or undefined otherwise.
 */
export function getUpdateSpans(state: SpansState): number {
  return state.updateSpans;
}

/**
 * Retrieve the fetchLimit value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - The fetchLimit number value if defined or undefined otherwise.
 */
export function getFetchLimit(state: SpansState): number | undefined {
  return state.fetchLimit;
}

/**
 * Retrieve the streamingIsPaused value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - The streamingIsPaused boolean value.
 */
export function getStreamingIsPaused(state: SpansState): boolean | undefined {
  return state.streamingIsPaused;
}

/**
 * Retrieve the boolean value used to show or hide the perspective table
 * based on existence of the spans table
 * @param state - Vuex state for the Spans module.
 * @returns - The shouldShowTable boolean value.
 */
export function getShouldShowTable(state: SpansState): boolean {
  return state.shouldShowTable;
}

/**
 * Retrieve the boolean value that checks if new dynamic columns were found.
 * @param state - Vuex state for the Spans module.
 * @returns - The dynamicColumns array.
 */
export function getDynamicColumns(state: SpansState): Array<string> {
  return state.dynamicColumns;
}

/**
 * Retrieve the stored dataProcessed in bytes.
 * @param state - Vuex state for the Spans module.
 * @returns - The dataProcessed stored value.
 */
export function getDataProcessed(state: SpansState): number {
  return state.dataProcessed;
}

/**
 * Retrieve the stored dataReturned in bytes.
 * @param state - Vuex state for the Spans module.
 * @returns - The dataReturned stored value.
 */
export function getDataReturned(state: SpansState): number {
  return state.dataReturned;
}

/**
 * Retrieve the updateWarehouse value of the Spans module.
 * @param state - Vuex state for the Spans module.
 * @returns - The updateWarehouse number value.
 */
export function getUpdateWarehouse(state: SpansState): number {
  return state.updateWarehouse;
}

/**
 * Retrieve the number of spans in memory.
 * @param state - Vuex state for the Spans' module.
 * @returns - The number of spans in memory.
 */
export function getSpansCount(state: SpansState): number {
  return state.spansCount;
}

/**
 * Retrieve the hasReportedSpans value stored in Vuex
 * @param state - Vuex state for the Spans module.
 * @returns - The value of hasReportedSpan in the Span Vuex module
 */
export function getHasReportedSpans(state: SpansState): boolean {
  return state.hasReportedSpans;
}

/**
 * Retrieve the tableConfiguration value stored in Vuex.
 * @param state - Vuex state for the Spans module.
 * @returns - The value of the tableConfiguration state.
 */
export function getTableConfiguration(
  state: SpansState
): PerspectiveViewConfig {
  const objString = JSON.stringify(state.tableConfiguration);
  return JSON.parse(objString);
}

/**
 * Retrieve the applyConfiguration value stored in Vuex.
 * @param state - Vuex state for the Spans module.
 * @returns - The value of the applyConfiguration state.
 */
export function getApplyConfiguration(state: SpansState): boolean {
  return state.applyConfiguration;
}

/**
 * Retrieve the tableSchema value stored in Vuex.
 * @param state - Vuex state for the Spans module.
 * @returns - The value of the tableSchema state.
 */
export function getTableSchema(state: SpansState): Record<string, string> {
  const objString = JSON.stringify(state.tableSchema);
  return JSON.parse(objString);
}

/**
 * Retrieve the shouldClearTable value stored in Vuex.
 * @param state - Vuex state for the Spans module.
 * @returns - The value of the shouldClearTable state.
 */
export function getShouldClearTable(state: SpansState): boolean {
  return state.shouldClearTable;
}

/**
 * Retrieve the splitByAttribute value of the Metrics module.
 * @param state - Vuex state for the Metrics module.
 * @returns - The splitByAttribute value.
 */
export function getSplitByAttribute(state: SpansState): string | undefined {
  return state.splitByAttribute;
}

/**
 * Retrieve the InMemoryFilters for the Spans view
 * @param state - Vuex state for the Spans module.
 * @returns Complete set of InMemoryFilters
 */
export function getInMemoryFilters(state: SpansState): SpansInMemoryFilters {
  return state.inMemoryFilters;
}
